import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getAgentSessionTrace,
  type WebAgentSessionTrace,
  type WebAgentSessionTraceToolCall,
} from '@/website/lib/chat-api';

export function AgentSessionDebugPage() {
  const { agentId = '', sessionId = '' } = useParams();
  const [trace, setTrace] = useState<WebAgentSessionTrace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError(null);
    void getAgentSessionTrace(sessionId, { agentId, limit: 500 })
      .then(nextTrace => {
        if (!disposed) setTrace(nextTrace);
      })
      .catch(err => {
        if (!disposed)
          setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });
    return () => {
      disposed = true;
    };
  }, [agentId, sessionId]);

  const usageLabel = useMemo(() => {
    const usage = trace?.usage;
    if (!usage) return 'No token usage';
    const total = usage.totalTokens ?? 0;
    const prompt = usage.promptTokens ?? 0;
    const completion = usage.completionTokens ?? 0;
    return `${total} tokens (${prompt} in, ${completion} out)`;
  }, [trace]);

  return (
    <div className="h-[100dvh] overflow-auto bg-background text-foreground">
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
        <header className="flex flex-col gap-2 border-b border-border pb-4">
          <Link
            to="/chat"
            className="w-fit text-sm text-muted-foreground hover:text-foreground"
          >
            Back to chat
          </Link>
          <div>
            <h1 className="text-xl font-semibold">Session debug</h1>
            <p className="mt-1 break-all text-sm text-muted-foreground">
              Agent {agentId} - Session {sessionId}
            </p>
          </div>
        </header>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading trace...</div>
        ) : error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : trace ? (
          <>
            <section className="grid gap-3 sm:grid-cols-3">
              <DebugStat
                label="Tool calls"
                value={String(trace.toolCalls.length)}
              />
              <DebugStat label="Usage" value={usageLabel} />
              <DebugStat
                label="More available"
                value={trace.hasMore ? 'Yes' : 'No'}
              />
            </section>

            {trace.skillReads.length > 0 ? (
              <section className="rounded-lg border border-border bg-card p-4">
                <h2 className="text-sm font-semibold">Skill reads</h2>
                <div className="mt-3 flex flex-col gap-2">
                  {trace.skillReads.map(read => (
                    <div
                      key={`${read.path}-${read.timestamp ?? ''}`}
                      className="break-all text-sm"
                    >
                      <span className="text-foreground">{read.path}</span>
                      {read.timestamp ? (
                        <span className="ml-2 text-muted-foreground">
                          {read.timestamp}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-3">
              {trace.toolCalls.length === 0 ? (
                <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                  No tool calls recorded.
                </div>
              ) : (
                trace.toolCalls.map((call, index) => (
                  <ToolCallCard
                    key={`${call.callId ?? index}-${index}`}
                    call={call}
                    index={index}
                  />
                ))
              )}
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}

function DebugStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-words text-sm font-medium">{value}</div>
    </div>
  );
}

function ToolCallCard({
  call,
  index,
}: {
  call: WebAgentSessionTraceToolCall;
  index: number;
}) {
  return (
    <article className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold">
          {index + 1}. {call.name}
        </h2>
        {call.status ? (
          <span className="text-xs text-muted-foreground">{call.status}</span>
        ) : null}
        {call.durationMs ? (
          <span className="text-xs text-muted-foreground">
            {call.durationMs}ms
          </span>
        ) : null}
        {call.timestamp ? (
          <span className="text-xs text-muted-foreground">
            {call.timestamp}
          </span>
        ) : null}
      </div>
      {call.callId ? (
        <div className="mt-1 break-all text-xs text-muted-foreground">
          {call.callId}
        </div>
      ) : null}
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <TraceJSON
          label={call.inputTruncated ? 'Input (truncated)' : 'Input'}
          value={call.input}
        />
        <TraceJSON
          label={call.outputTruncated ? 'Output (truncated)' : 'Output'}
          value={call.output}
        />
      </div>
    </article>
  );
}

function TraceJSON({ label, value }: { label: string; value?: string }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-xs font-medium text-muted-foreground">
        {label}
      </div>
      <pre className="max-h-[360px] overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed">
        {formatMaybeJSON(value)}
      </pre>
    </div>
  );
}

function formatMaybeJSON(value?: string) {
  if (!value?.trim()) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}
